import React, { useEffect, useRef } from 'react';
import ErrorMessage from './ErrorMessage';
import Dropdown from './Dropdown';
import DropdownSkeleton from './DropdownSkeleton';
import Label from '../common/Label';
import useDeviceSelector from '../../hooks/useDeviceSelector';
import { USER_ROLES } from '../../constants';
import ClearButton from './ClearButton';

/**
 * Renders three selectors to choose the right device unit
 * Selector 1 -  Organization Level, It's only shown for Admin Roles
 * Selector 2 -  Facility Level, it should always be shown
 * Selector 3 - Device Unit Level, it should always be shown
 *
 * Handles disabilities and logic
 *
 * Returns
 */
const DeviceUnitSelector = ({ showLabel = false }) => {
  const {
    selectedFacility,
    selectedOrganization,
    selectedFacilityName,
    selectedUnit,
    userType,
    isLoading,
    handleClearFilters,
    facilities,
    organizationOptions,
    facilityOptions,
    unitOptions,
    selectedOrganizationName,
    unitError,
    facilityError,
    handleSelect,
    handleSearch,
    loading,
  } = useDeviceSelector();

  const userIsAdmin = userType === USER_ROLES.ADMIN;
  const disableFacilitySelector = userIsAdmin
    ? !selectedOrganization || facilities.length === 0
    : false;

  const disableUnitSelector = !unitOptions || unitOptions?.length === 0;

  // Consolidated loading flags
  const isOrganizationsLoading = loading?.organizations || isLoading;
  const isFacilitiesLoading = loading?.facilities || isLoading;
  const isUnitsLoading = loading?.units || isLoading;

  // --- Auto-search cuando TODO está seleccionado ---
  const lastSearchKeyRef = useRef('');

  useEffect(() => {
    // no ejecutar si hay dropdowns cargando o una búsqueda en curso (si tu hook lo marca)
    if (isOrganizationsLoading || isFacilitiesLoading || isUnitsLoading || loading?.search) return;

    // helper para clave única de criterios (evita búsquedas duplicadas)
    const buildSearchKey = () => {
      // Admin debe tener org; no-admin lo omitimos
      const hasOrg = userIsAdmin ? !!selectedOrganization : true;
      if (!hasOrg || !selectedFacility || !selectedUnit) return null;

      const orgKey = selectedOrganization?.id || selectedOrganizationName || 'NA';

      const facilityKey =
        selectedFacility?.id || selectedFacility?.facility_id || selectedFacilityName || 'NA';

      const unitKey =
        selectedUnit?.esp32_mac_address || selectedUnit?.id || selectedUnit?.data_unit_name || 'NA';

      return `${orgKey}|${facilityKey}|${unitKey}`;
    };
    const key = buildSearchKey();
    if (!key) return;

    if (lastSearchKeyRef.current !== key) {
      lastSearchKeyRef.current = key;
      handleSearch(); // dispara automáticamente cuando están las 2/3 selecciones
    }
  }, [
    // deps que impactan la selección efectiva
    userIsAdmin,
    selectedOrganization,
    selectedOrganizationName,
    selectedFacility,
    selectedFacilityName,
    selectedUnit,
    // loaders
    isOrganizationsLoading,
    isFacilitiesLoading,
    isUnitsLoading,
    loading?.search,
    handleSearch,
  ]);

  // Helper para dropdown con skeleton + error
  const renderDropdown = ({
    condition,
    options,
    selectedOption,
    onSelect,
    text,
    widthClass,
    disabled = false,
    error,
  }) => {
    if (condition) {
      return (
        <div className="relative min-h-[2rem]">
          <DropdownSkeleton widthClass={widthClass} />
        </div>
      );
    }
    return (
      <div className="relative min-h-[2rem]">
        <Dropdown
          options={options}
          selectedOption={selectedOption}
          onSelect={onSelect}
          text={text}
          widthClass={widthClass}
          disabled={disabled}
        />
        {error && <ErrorMessage message={error} />}
      </div>
    );
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-6 w-full sm:w-auto">
      {/* Organization (solo admin) */}
      {userIsAdmin && (
        <div className="w-full sm:w-auto">
          {showLabel && <Label variant="dark">Organization</Label>}

          {isOrganizationsLoading ? (
            <div className="relative min-h-[2rem]">
              <DropdownSkeleton widthClass="w-full sm:w-64" />
            </div>
          ) : (
            <Dropdown
              options={organizationOptions}
              selectedOption={selectedOrganizationName}
              onSelect={(name) => handleSelect('organization', name)}
              text="Choose Organization"
              widthClass="w-full sm:w-64"
            />
          )}
        </div>
      )}

      {/* Facility */}
      <div className="w-full sm:w-auto">
        {showLabel && <Label variant="dark">Facility</Label>}

        {renderDropdown({
          condition: isOrganizationsLoading || isFacilitiesLoading,
          options: facilityOptions,
          selectedOption: selectedFacilityName,
          onSelect: (name) => handleSelect('facility', name),
          text: 'Choose Facility',
          widthClass: 'w-full sm:w-64',
          disabled: disableFacilitySelector,
          error: facilityError,
        })}
      </div>

      {/* Unit */}
      <div className="w-full sm:w-auto">
        {showLabel && <Label variant="dark">Unit</Label>}

        {renderDropdown({
          condition: isOrganizationsLoading || isFacilitiesLoading || isUnitsLoading,
          options: unitOptions,
          selectedOption: selectedUnit?.data_unit_name || '',
          onSelect: (name) => handleSelect('unit', name),
          text: 'Choose Unit',
          widthClass: 'w-full sm:w-64',
          disabled: disableUnitSelector,
          error: unitError,
        })}
      </div>

      {/* Clear Filters (opcional dejarlo siempre visible) */}
      <ClearButton onClick={handleClearFilters} />
    </div>
  );
};

export default DeviceUnitSelector;
